<template>
  <div class="bg-surface-default-mid relative mb-48">
    <RevContainer
      class="bg-static-info-mid flex items-center justify-center p-32 md:p-56"
    >
      <div class="mx-auto flex max-w-[326px] flex-col items-center">
        <RevIllustration
          :alt="i18n(translations.imageAlt)"
          :height="180"
          :src="'/img/buyback/buybackFunnel.svg'"
          :width="329"
        />

        <h1 class="text-static-default-mid heading-1 mt-32 text-center">
          {{ i18n(translations.title) }}
        </h1>

        <span class="text-static-default-low body-1 mt-2 text-center">
          {{ i18n(translations.subtitle) }}
        </span>
        <div class="mt-32 hidden md:block md:w-full">
          <RevButton
            data-test="my-sales-link"
            full-width="always"
            :to="{
              name: DASHBOARD.MY_SALES,
            }"
            variant="primary"
          >
            {{ i18n(translations.mySalesLink) }}
          </RevButton>
        </div>
      </div>
    </RevContainer>

    <RevContainer class="mt-48 md:px-12">
      <template v-if="hasConfirmationDetails">
        <h3 class="heading-3 mb-24">{{ i18n(translations.nextStepsTitle) }}</h3>

        <Instructions
          data-test="instructions"
          :days-left="confirmationDetails?.daysLeft"
          :model="confirmationDetails?.title"
          :price="confirmationDetails?.price"
          :shipping-mode="confirmationDetails?.shippingMode"
        />

        <RevInfoBlock
          class="mt-24 hidden md:flex"
          :icon="IconBellOutlined"
          :title="i18n(translations.infoBlockMessage)"
          variant="default"
        />
      </template>

      <RevSkeleton v-else class="h-320 md:h-200" shape="rectangle" />
    </RevContainer>

    <RevContainer class="mt-48 md:px-12">
      <template v-if="hasConfirmationDetails">
        <h3 class="heading-3">{{ i18n(translations.saleInfo) }}</h3>

        <div class="mt-24 items-start gap-32 md:grid md:grid-cols-3">
          <RevCard class="mb-16 p-32 md:mb-0">
            <ProductInfo
              :image="productImage"
              :price="confirmationDetails?.price"
              :title="confirmationDetails?.title"
            />
            <RevList
              class="border-static-default-low border-b-1 md:border-b-0"
              :has-external-borders="false"
            >
              <RevListItemStatic
                v-for="item in diagnosticDetails"
                :key="item.id"
              >
                <template #label>
                  <div class="flex justify-between">
                    <p class="body-2">{{ item.label }}</p>
                    <p class="body-2-bold text-right">{{ item.value }}</p>
                  </div>
                </template>
              </RevListItemStatic>
              <RevListItemStatic v-for="item in orderDetails" :key="item.id">
                <template #label>
                  <div class="flex justify-between">
                    <p class="body-2">{{ item.label }}</p>
                    <p class="body-2-bold text-right">{{ item.value }}</p>
                  </div>
                </template>
              </RevListItemStatic>
            </RevList>
          </RevCard>
          <MobileAppBanner class="col-span-2 mb-40 md:mb-0" />
        </div>
      </template>

      <RevSkeleton v-else class="h-[950px] md:!h-[500px]" shape="rectangle" />
    </RevContainer>

    <RevStickyBar class="md:hidden">
      <RevButton full-width="always" :to="dashboardRoute" variant="primary">
        {{ i18n(translations.mySalesLink) }}
      </RevButton>
    </RevStickyBar>
  </div>
</template>

<script setup lang="ts">
import { useAsyncData, useRoute, useRouteParams } from '#imports'
import { computed, inject, onMounted, ref, watch } from 'vue'

import ProductInfo from '@backmarket/nuxt-layer-buyback/components/ProductInfo/ProductInfo.vue'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevContainer } from '@ds/components/Container'
import { RevIllustration } from '@ds/components/Illustration'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevList } from '@ds/components/List'
import { RevListItemStatic } from '@ds/components/ListItemStatic'
import { RevSkeleton } from '@ds/components/Skeleton'
import { RevStickyBar } from '@ds/components/StickyBar'
import { IconBellOutlined } from '@ds/icons/IconBellOutlined'

import type { CustomerSaleDetailsPayload } from '~/api/buyback-orders/types'
import MobileAppBanner from '~/scopes/buyback/components/MobileAppBanner/MobileAppBanner.vue'
import { BUYBACK_PARTNERS } from '~/scopes/buyback/constants'
import { ROUTE_NAMES } from '~/scopes/dashboard/constants'

import { confirmationDetailsAdapter } from '../adapters/confirmationDetails'
import Instructions from '../components/Instructions/Instructions.vue'
import type { ErrorData } from '../components/TheCatcher/useCatcher'
import { useBuybackFunnelOffer } from '../composables/useBuybackFunnelOffer'

import translations from './Confirmation.translations'
import { FUNNEL_ERROR_MESSAGE } from './constants'

const { DASHBOARD } = ROUTE_NAMES

const tracking = useTracking()
const $httpFetch = useHttpFetch()
const route = useRoute()
const i18n = useI18n()
const routeParams = useRouteParams()
const { resetBuyBackOffer } = useBuybackFunnelOffer()
const logger = useLogger()

const errorData = inject<ErrorData>('errorData', ref({ message: '', step: '' }))
errorData.value = {
  message: FUNNEL_ERROR_MESSAGE.BUYBACK_FUNNEL,
  step: 'Confirmation',
}

const { data: confirmationDetails, error: confirmationDetailsError } =
  useAsyncData(
    async () => {
      return $httpFetch('/bm/buyback/v1/customer/orders/:orderPublicId', {
        pathParams: { orderPublicId: routeParams?.id },
      })
    },
    {
      transform: (response: CustomerSaleDetailsPayload | null) => {
        if (!response) return null

        return confirmationDetailsAdapter(response)
      },
    },
  )

watch(confirmationDetailsError, () => {
  if (!confirmationDetailsError.value) return

  logger.error(FUNNEL_ERROR_MESSAGE.GET_CONFIRMATION_DETAILS, {
    statusCode: confirmationDetailsError.value.statusCode,
    statusMessage: confirmationDetailsError.value.message,
    owners: ['bot-squad-circularity-offer-front'],
  })
})

const hasConfirmationDetails = computed(
  () =>
    confirmationDetails.value?.title &&
    confirmationDetails.value?.price &&
    confirmationDetails.value?.shippingMode &&
    confirmationDetails.value?.details,
)

const productImage = computed(() => {
  return hasConfirmationDetails.value && confirmationDetails.value?.image?.light
})

const diagnosticDetails = computed(() => {
  return confirmationDetails.value?.details[0]
})

const orderDetails = computed(() => {
  return confirmationDetails.value?.details[1]
})

const dashboardRoute = {
  name: DASHBOARD.MY_SALES,
}

onMounted(() => {
  resetBuyBackOffer()

  if (route?.query?.partner === BUYBACK_PARTNERS.PLAYSTATION) {
    tracking.trackBuyBackPlayStationPartnership()
  }
})
</script>
